import React, { forwardRef, useImperativeHandle } from "react";
import { Dialog, DialogTitle, Button, DialogContent, DialogActions, TextField, MenuItem, Chip, Tooltip, Autocomplete } from "@mui/material"
import { useCallback, useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { remakeOrderOptions } from "../../Common/staticjson";
import { MyDiv } from "../../Common/Components";
import Swal from "sweetalert2";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const RemakeOrder = forwardRef(({ open, handleClose, order, loadSpecificOrder, totalPrice, order_unique_id, formData = {} }, ref) => {
    const [btnLoading, setBtnLoading] = useState(false);
    const [data, setData] = useState({
        old_order_unique_id: "",
        reason: "",
        department: "",
        emp_id: "",
        emp_name: "", 
        total_price: "",
        approved_by: "", 
        approved_person: "",
        description: ""
    });

    const [departments, setDepartments] = useState([])
    const [empData, setEmpData] = useState({})
    const [drivers, setDrivers] = useState([])
    const [users, setUsers] = useState([])
    const [selectedReason, setSelectedReason] = useState(null)
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [roles, setRoles] = useState([]);

    function handle(e) {
        const newData = { ...data };
        newData[e.target.id || e.target.name] = e.target.value;
        if(e.target.id === 'department' || e.target.name === 'department') {
            setEmpData({})
            newData["emp_id"] = null
            newData["emp_name"] = ""
            // Call getEmpName immediately with the new department value
            getEmpName(order?.old_order_unique_id, newData.department)
        }
        setData(newData);
    }

    const getActiveUsers = useCallback(async (role) => {
        try {
            const response = await fetch(`${API_BASE_URL}fetch-active-users/${role}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': localStorage.getItem("token"),
                    'Accept': 'application/json',
                },
            });
            const result = await response.json();
            setUsers(result)
        } catch (error) {
            console.log(error)
        }
    },[])

    const getEmpName = useCallback(async (order_number, dept) => {
        if(!dept || !order_number) return;
        
        setBtnLoading(true)
        try {
            const response = await fetch(`${API_BASE_URL}get-employee-by-order?order_unique_id=${order_number}&dept=${dept}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': localStorage.getItem("token"),
                    'Accept': 'application/json',
                },
            });
            const result = await response.json();
            if(result?.message?.includes("not")){
                Swal.fire({ text: result?.message, icon: "info", type: "info" });
                setEmpData({})
                setBtnLoading(false)
                return
            }
            if(result.length > 0){
                setEmpData(result[0])
            }
            setBtnLoading(false)
        } catch (error) {
            console.log(error)
            setEmpData({})
            setBtnLoading(false);
        }
    },[])

    useEffect(() =>{
        if(order?.old_order_unique_id){
            setData(prevData => ({
                ...prevData,
                old_order_unique_id: order?.old_order_unique_id
            }))
        }
    },[order?.old_order_unique_id, formData?.old_order_unique_id])

    useEffect(() => {
        const fetchData = async () => {
            if (formData?.old_order_unique_id) {

                const reasonOption = remakeOrderOptions.find(
                    item => item.value === Number(formData.reason)
                )

                setDepartments(reasonOption?.options || [])
                setSelectedReason(reasonOption)

                await getEmpName(formData.old_order_unique_id, formData.department)
                await getActiveUsers(formData.approved_by)

                const formattedData = {
                    ...formData,
                    reason: Number(formData.reason),
                    department: formData.department,
                    emp_id: formData.department === "DRIVER" ? formData?.emp_id[0] : formData?.emp_id,
                }

                setData(formattedData)
            }
        }

        fetchData()
    }, [formData?.old_order_unique_id, formData?.reason, formData?.department, formData?.approved_by, getEmpName, getActiveUsers])


    const handleReason = (e) => {
        let value = e.target.value
        const reasonOption = remakeOrderOptions.find(item => item.value === value)
        setSelectedReason(reasonOption)
        setData({
            ...data, 
            reason: value, 
            department: "", 
            emp_id: "",
            emp_name: ""
        })
        setDepartments(reasonOption?.options || [])
        setEmpData({})
        setSelectedFiles([])
    }

    const handleFileSelect = e => {
        setSelectedFiles(Array.from(e.target.files));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        
        // Validate old and new order numbers are different
        if(data.old_order_unique_id === order_unique_id || order.old_order_unique_id === order_unique_id){
            Swal.fire({ text: "Old and Remake order number cannot be same.", icon: "warning", type: "warning" });
            return
        }

        // Validate employee selection/name entry for specific cases
        const showDriverSelector = data.reason === 3 && data.department === "DRIVER";
        const showManualNameEntry = (
            (data.reason === 3 && data.department !== "DRIVER") ||
            data.reason === 4 || 
            data.reason === 5
        );
        
        if(showDriverSelector && !data.emp_id) {
            Swal.fire({ text: "Please select a driver.", icon: "warning", type: "warning" });
            return
        }

        // if(showManualNameEntry && !data.emp_name) {
        //     Swal.fire({ text: "Please enter employee name.", icon: "warning", type: "warning" });
        //     return
        // }

        setBtnLoading(true);
        const formData = new FormData();

        // If reason has no department options (reason 4 or 5), use the reason label as department
        let departmentValue = data.department;
        if (selectedReason && (!selectedReason.options || selectedReason.options.length === 0)) {
            departmentValue = selectedReason.label;
        }


        const processedData = { 
            order_unique_id: order_unique_id,
            total_price: totalPrice,
            order_remake_checker: order.order_remake_checker,
            new_order_unique_id: order_unique_id,
            old_order_unique_id: order.old_order_unique_id || data.old_order_unique_id,
            reason: data.reason,
            approved_by: data.approved_by,
            approved_person: data.approved_person,
            department: departmentValue,
            description: data.description,
            attachment: !selectedFiles.length ? data?.attachment : ""
        }

        // Handle employee data
        if (showManualNameEntry && data.emp_name) {
            // Manual name entry case - no emp_id
            processedData.emp_id = JSON.stringify([]);
            processedData.emp_name = data.emp_name;
        } else if (data.department === "DRIVER" && data.emp_id) {
            // Driver selection case
            processedData.emp_id = JSON.stringify([data.emp_id]);
        } else if (Array.isArray(empData?.emp_ids) && empData.emp_ids.length > 0) {
            // Auto-fetched multiple employees from old order
            processedData.emp_id = JSON.stringify(empData.emp_ids);
        } else if (empData?.emp_id) {
            // Auto-fetched single employee from old order
            processedData.emp_id = JSON.stringify([empData.emp_id]);
        } else {
            processedData.emp_id = JSON.stringify([]);
        }

        // First, append all form data
        for (const key in processedData) {
            const value = processedData[key];
            formData.append(key, value ?? "");
        }

        // Then append files - IMPORTANT: use 'attachments' as the key, not 'documents'
        selectedFiles.forEach(file => {
            formData.append("documents", file);
        });
        
        const url = `${API_BASE_URL}create-remake-order`;
        const method = "post";
        axios({
            method,
            url,
            data: formData,
            headers: { 
                "x-access-token": localStorage.getItem("token"), 
                Accept: "application/json", 
                "Content-Type": "multipart/form-data" 
            },
        })
        .then(res => {
            if (res.status === 200) {
                Swal.fire({ text: res.data, icon: "success", type: "success", timer: 2000 });
                loadSpecificOrder()
                handleClose();
                resetForm();
            }
            setBtnLoading(false);
        })
        .catch(error => {
            Swal.fire({ text: error.response?.data || error.message, icon: "error", type: "error" });
            setBtnLoading(false);
        })
    }

    const resetForm = () => {
        setData({
            old_order_unique_id: "",
            reason: "",
            department: "",
            emp_id: "",
            emp_name: "",
            approved_by: "",
            approved_person: "",
            total_price: "",
            description: ""
        })
        // setDepartments([])
        setEmpData({})
        setSelectedReason(null)
        setSelectedFiles([])
    }

    useImperativeHandle(ref, () => ({
        resetForm
    }));

    const getActiveDrivers = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}fetch-active-drivers`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': localStorage.getItem("token"),
                    'Accept': 'application/json',
                },
            });
            const result = await response.json();
            setDrivers(result?.data)
        } catch (error) {
            console.log(error)
        }
    },[])

    const loadUserRole = useCallback(async () => {
    try {
      const url = `${API_BASE_URL}fetch-role-data-dropdown?remake=true`;
      const response = await axios.get(url, {
        headers: {
          "x-access-token": localStorage.getItem("token"),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      const additionalData = [
            { _id : "Customer Photos", role_Name : "Customer Photos" },
            { _id : "Missing", role_Name : "Missing" }
        ]
      setRoles([...response.data, ...additionalData] || []);
    } catch (error) {
      Swal.fire({
        text: error.response?.data?.message || "Failed to fetch roles",
        icon: "error",
      });
    }
  }, []);

    useEffect(() => {
        if(data.department) {
            getEmpName(data.old_order_unique_id, data.department)
        }
    },[data.old_order_unique_id, data.department, getEmpName])

    useEffect(() => {
        if(open) {
            getActiveDrivers()
            loadUserRole()
            // resetForm()
        }
    },[open, getActiveDrivers, loadUserRole])

    // Determine if employee selector should be shown
    const showEmployeeSelector = (
        (data.reason === 3 && (data.department === "DRIVER" || data.department === "LOADER")) || 
        data.reason === 4 || 
        data.reason === 5
    );

    // Determine if manual name entry should be shown
    const showManualNameEntry = (
        (data.reason === 3 && data.department !== "DRIVER") ||
        data.reason === 4 || 
        data.reason === 5
    );

    const showDriverSelector = (
        data.reason === 3 && data.department === "DRIVER"
    );

    const handleChangeApprovedBy = async (e) => {
        const { name, value } = e.target
        setData({
            ...data,
            [name] : value,
            approved_person: null
        })
        await getActiveUsers(value)
    }

    return(
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" className="GeneralModal">
            <DialogTitle className="space-between">
                Remake Order Details - {order?.old_order_unique_id || data?.old_order_unique_id}
                <Button onClick={handleClose} className="btn">
                    X
                </Button>
            </DialogTitle>
            <form onSubmit={(e) => onSubmit(e)}>
            <DialogContent>
                <Row className="row-gap-3">
                    <Col md={6}>
                        <TextField 
                            id="approved_by" 
                            name="approved_by" 
                            fullWidth 
                            required 
                            select 
                            label="Approved By" 
                            value={data.approved_by} 
                            onChange={e => handleChangeApprovedBy(e)} 
                            variant="outlined"
                        >
                            {roles.map(option => (
                                <MenuItem key={option._id} value={option.role_Name}>
                                    {option.role_Name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Col>
                    <Col md={6}>
                        {users.length ?
                        <Autocomplete
                            options={users || []}
                            getOptionLabel={(option) => `${option.user_firstName + " " + option.user_lastName|| ""}`.trim()}
                            isOptionEqualToValue={(option, value) => option._id === value?._id}
                            value={users?.find(u => u._id === data.approved_person) || null}
                            onChange={(e, value) => {
                                setData(prev => ({ ...prev, approved_person: value?._id || "" }));
                            }}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth label="Approved Person" variant="outlined" required />
                            )}
                        /> : null
                        }
                    </Col>
                    <Col md={6}>
                        <TextField 
                            id="reason" 
                            name="reason" 
                            fullWidth 
                            required 
                            select 
                            label="Reason" 
                            value={data.reason} 
                            onChange={e => handleReason(e)} 
                            variant="outlined"
                        >
                            {remakeOrderOptions.map(option => (
                                <MenuItem key={option.id} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Col>
                    {departments?.length > 0 && 
                        <>
                            <Col md={6}>
                                <TextField 
                                    id="department" 
                                    name="department" 
                                    fullWidth 
                                    required 
                                    select 
                                    label="Department" 
                                    value={data.department} 
                                    onChange={e => handle(e)} 
                                    variant="outlined"
                                >
                                    {departments.map(option => (
                                        <MenuItem key={option.id} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Col>
                            {!showEmployeeSelector && !showManualNameEntry && (() => {
                                const userEntries = empData ? Object.entries(empData).filter(([key, val]) => (key.includes('_user_name') || key.includes('_person_name')) && val) : [];
                                if (userEntries.length > 0) {
                                    return (
                                        <Col md={6}>
                                            <MyDiv style={{ display: 'flex', gap: 8, alignItems: "center", flexWrap: 'wrap' }}>
                                                {userEntries.map(([key, val], index) => {
                                                    let action = key.includes('_user_name') ? key.split("_")[2] : key.split("_")[1]
                                                    return(
                                                        <Tooltip key={index} title={action}>
                                                            <Chip label={val}/>
                                                        </Tooltip>
                                                    )}
                                                )}
                                            </MyDiv>
                                        </Col>
                                    );
                                } else if(data.department) {
                                    return (
                                        <Col md={6}>
                                            <MyDiv style={{ display: 'flex', gap: 8, alignItems: "center" }}>
                                                {data.department !== "UNKNOWN"  &&
                                                <Chip label= {`Department: ${departments.find(d => d.value === data.department)?.label || data.department}`}/>}
                                            </MyDiv>
                                        </Col>
                                    );
                                }
                            })()}
                        </>
                    }
                    {showDriverSelector && 
                        <Col md={6}>
                            <Autocomplete
                                options={drivers || []}
                                getOptionLabel={(option) => `${option.name || ""}`.trim()}
                                isOptionEqualToValue={(option, value) => option._id === value?._id}
                                value={drivers?.find(u => u._id === data.emp_id) || null}
                                onChange={(e, value) => {
                                    setData(prev => ({ ...prev, emp_id: value?._id || "" }));
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth label="Driver Name" variant="outlined" required />
                                )}
                            />
                        </Col>
                    }
                    {showManualNameEntry && 
                        <Col md={6}>
                            <TextField
                                id="emp_name" 
                                fullWidth 
                                required={data?.department === "F" || data?.department === "FG" || data?.department === "ROOF" || data?.department === "CL" || data?.department === "GBI" || data?.department === "J"}
                                label="Employee Name" 
                                value={data.emp_name} 
                                onChange={e => handle(e)} 
                                variant="outlined"
                                placeholder="Enter employee name"
                            />
                        </Col>
                    }
                    <Col md={12}>
                        {/* <LabelTag>Description {data.reason === 5 && <span style={{color: 'red'}}>*</span>}</LabelTag> */}
                        <TextField 
                            fullWidth
                            multiline
                            rows={6}
                            required={data.reason === 5 || data.reason === 4 || data.department === "UNKNOWN"}
                            id="description" 
                            label="Description" 
                            value={data.description} 
                            onChange={e => handle(e)} 
                            variant="outlined"
                        />
                    </Col>
                </Row>
            </DialogContent>
            <DialogActions className="d-flex justify-content-between">
                <MyDiv className="mb-2">
                    <input 
                        type="file" 
                        accept="application/pdf,image/*" 
                        id="document" 
                        disabled={(!order?.old_order_unique_id && !data?.old_order_unique_id) || !data.reason} 
                        name="document" 
                        multiple 
                        onChange={handleFileSelect} 
                    />
                    {!selectedFiles.length ? <span>{data?.attachment}</span> : ""}
                    
                </MyDiv>
                <Button className="btn primary-btn mb-1" type="submit" disabled={btnLoading}>
                    {btnLoading ? <Spinner animation="border" size="sm" /> : "Submit"}
                </Button>
            </DialogActions>
            </form>
        </Dialog>
    )
})

export default RemakeOrder;